<template>
  <div class="region-input-wrapper">
    <div class="region-input"
         :class="{ 'region-input--error': !!props.error }">
      <select id="region-select"
              :value="modelValue"
              class="region-input-select"
              :disabled="props.disabled"
              v-bind="restAttrs"
              @input="onSelectChange">
        <option value=""
                disabled>{{ placeholder }}</option>
        <option v-for="opt in options"
                :key="opt.value"
                :value="opt.value">{{ opt.label }}</option>
      </select>
    </div>
    <UIError :message="props.error" />
  </div>
</template>

<script setup lang="ts">
import { useAttrs } from 'vue'
import UIError from '@/components/UI/UIError.vue'
import type { RegionOption } from '@/composables/region/useRegionOptions'

interface Props {
  modelValue?: string
  /** Список опцій [{ value, label }]. Передається ззовні (напр. з useRegionOptions). */
  options?: RegionOption[]
  placeholder?: string
  error?: string
  disabled?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  options: () => [],
  placeholder: '',
  disabled: false,
})

const emit = defineEmits<Emits>()

const attrs = useAttrs() as Record<string, unknown>
const { value: _value, onInput: _onInput, ...restAttrs } = attrs

function onSelectChange(e: Event) {
  const target = e.target as HTMLSelectElement
  emit('update:modelValue', target.value ?? '')
}

defineOptions({ inheritAttrs: false })
</script>

<style scoped>
.region-input-wrapper {
  width: 100%;
}

.region-input {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 56px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 0 14px 0 16px;
  background: #fff;
}

.region-input--error {
  border: 2px solid var(--color-error, #ff3b66);
}

.region-input--error:focus-within {
  outline: none;
  border-color: var(--color-error, #ff3b66);
}

.region-input-select {
  flex: 1;
  width: 100%;
  min-height: 54px;
  padding: 0;
  font-family: var(--font-inter);
  font-size: 16px;
  color: var(--color-dark-blue);
  border: none;
  background: transparent;
  cursor: pointer;
  appearance: auto;
}

.region-input-select:focus {
  outline: none;
}

.region-input-select:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
</style>
